import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Plus,
  Search,
  Play,
  Pause,
  Trash2,
  Edit,
  Clock,
  Zap,
  FileCode,
  MoreVertical
} from 'lucide-react';

interface Job {
  id: string;
  name: string;
  description: string;
  script: string;
  schedule: string;
  type: 'cron' | 'manual';
  enabled: boolean;
  created_at: string;
  updated_at: string;
  last_execution?: {
    status: 'success' | 'failed' | 'running';
    started_at: string;
  };
}

export default function JobsList() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'cron' | 'manual'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'enabled' | 'disabled'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [jobs, searchQuery, filterType, filterStatus]);

  const fetchJobs = async () => {
    try {
      const response = await axios.get('/api/jobs');
      setJobs(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load jobs');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...jobs];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(
        (job) =>
          job.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          job.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter((job) => job.type === filterType);
    }

    // Status filter
    if (filterStatus === 'enabled') {
      filtered = filtered.filter((job) => job.enabled);
    } else if (filterStatus === 'disabled') {
      filtered = filtered.filter((job) => !job.enabled);
    }

    setFilteredJobs(filtered);
  };

  const toggleJobStatus = async (jobId: string, currentStatus: boolean) => {
    try {
      await axios.patch(`/api/jobs/${jobId}`, { enabled: !currentStatus });
      await fetchJobs();
    } catch (err) {
      alert('Failed to toggle job status');
      console.error(err);
    }
  };

  const deleteJob = async (jobId: string) => {
    if (!confirm('Are you sure you want to delete this job?')) return;

    try {
      await axios.delete(`/api/jobs/${jobId}`);
      await fetchJobs();
    } catch (err) {
      alert('Failed to delete job');
      console.error(err);
    }
  };

  const runJobNow = async (jobId: string) => {
    try {
      await axios.post(`/api/jobs/${jobId}/execute`);
      alert('Job execution started');
    } catch (err) {
      alert('Failed to start job execution');
      console.error(err);
    }
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="text-white text-center">Loading jobs...</div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Jobs</h1>
          <p className="text-slate-400">Manage your scheduled and manual jobs</p>
        </div>
        <Link
          to="/jobs/new"
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>Create Job</span>
        </Link>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 text-red-200">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Type Filter */}
          <div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as 'all' | 'cron' | 'manual')}
              className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="cron">Cron Jobs</option>
              <option value="manual">Manual Jobs</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as 'all' | 'enabled' | 'disabled')}
              className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Jobs Count */}
      <div className="flex items-center gap-4 text-sm text-slate-400">
        <span>
          Showing <strong className="text-white">{filteredJobs.length}</strong> of{' '}
          <strong className="text-white">{jobs.length}</strong> jobs
        </span>
      </div>

      {/* Jobs Table */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
        {filteredJobs.length === 0 ? (
          <div className="p-12 text-center">
            <FileCode className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 mb-4">No jobs found</p>
            <Link
              to="/jobs/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Create Your First Job</span>
            </Link>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Job
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Schedule
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Last Execution
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {filteredJobs.map((job) => (
                <tr key={job.id} className="hover:bg-slate-700/50 transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-white font-medium">{job.name}</p>
                      <p className="text-sm text-slate-400 truncate max-w-md">{job.description}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                        job.type === 'cron'
                          ? 'bg-blue-900/50 text-blue-300'
                          : 'bg-purple-900/50 text-purple-300'
                      }`}
                    >
                      {job.type === 'cron' ? <Clock className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
                      {job.type.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <code className="text-sm text-slate-300 bg-slate-900 px-2 py-1 rounded">
                      {job.schedule || 'Manual'}
                    </code>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        job.enabled
                          ? 'bg-green-900/50 text-green-300'
                          : 'bg-slate-700 text-slate-400'
                      }`}
                    >
                      {job.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {job.last_execution ? (
                      <div>
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                            job.last_execution.status === 'success'
                              ? 'bg-green-900/50 text-green-300'
                              : job.last_execution.status === 'failed'
                              ? 'bg-red-900/50 text-red-300'
                              : 'bg-yellow-900/50 text-yellow-300'
                          }`}
                        >
                          {job.last_execution.status}
                        </span>
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(job.last_execution.started_at).toLocaleString()}
                        </p>
                      </div>
                    ) : (
                      <span className="text-sm text-slate-500">Never executed</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => runJobNow(job.id)}
                        className="p-2 text-blue-400 hover:bg-blue-900/50 rounded transition-colors"
                        title="Run Now"
                      >
                        <Play className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => toggleJobStatus(job.id, job.enabled)}
                        className={`p-2 rounded transition-colors ${
                          job.enabled
                            ? 'text-yellow-400 hover:bg-yellow-900/50'
                            : 'text-green-400 hover:bg-green-900/50'
                        }`}
                        title={job.enabled ? 'Disable' : 'Enable'}
                      >
                        {job.enabled ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </button>
                      <Link
                        to={`/jobs/${job.id}/edit`}
                        className="p-2 text-slate-400 hover:bg-slate-600 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => deleteJob(job.id)}
                        className="p-2 text-red-400 hover:bg-red-900/50 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
